import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/presentations/pathway-tutorials/ CBS & Transsulfuration series.

export const transsulfurationSarah: Scenario = {
  slug: 'cbs-transsulfuration-sarah',
  series: 'CBS & Transsulfuration',
  title: 'The supplement stack that made everything worse',
  socialHook:
    "Sarah, 41: six months of doing everything right — methylfolate, B12, NAC, glutathione — and feeling progressively worse. The clue was not in her homocysteine. It was in what her CBS gene was doing with her supplements.",
  meta: {
    description:
      "A fictional case study: Sarah's homocysteine was perfectly normal, but her fatigue, anxiety, and shoulder tension kept worsening on a standard methylation protocol. Her CBS and BHMT variants explain why the supplements were adding fuel to an overloaded sulfur pathway.",
  },

  persona: {
    name: 'Sarah',
    age: 41,
    sex: 'Female',
    ancestry: 'South Asian',
    occupation: 'Healthcare administrator',
    chiefConcern:
      "Six months on a methylation supplement stack — methylfolate, B12, NAC, glutathione — and I feel worse than when I started. More fatigue, new anxiety, jaw and shoulder tension I can't explain.",
    familyHistory:
      "Mum has IBS and sulphite sensitivity — reacts to wine and dried fruit. Dad takes metformin for type 2 diabetes.",
  },

  clinicalPicture: {
    symptoms: [
      "Fatigue worse than before she started supplementing",
      "Low-level anxiety — new since starting the protocol",
      "Jaw and shoulder tension she cannot release",
      "Sulphite sensitivity: reacts to wine, dried fruit, and processed foods",
      "Bloating and gut discomfort, worse since adding NAC",
    ],
    labs: [
      { name: 'Homocysteine', value: '6.2 µmol/L', refRange: '5–15 µmol/L', flag: 'normal' },
      { name: 'Urinary sulfite', value: 'Elevated', refRange: 'Negative / trace', flag: 'high' },
      { name: 'Serum taurine', value: 'Upper limit of normal', refRange: 'Reference range', flag: 'normal' },
    ],
    medications: ['None'],
    supplements: [
      'Methylfolate 400 mcg',
      'Methylcobalamin 1000 mcg',
      'NAC 600 mg',
      'Liposomal glutathione',
      'Magnesium glycinate',
    ],
    lifestyle: [
      'Whole-food diet',
      'Avoids processed foods — noticed she feels better without them',
    ],
  },

  genetics: [
    {
      gene: 'CBS',
      variant: 'C699T',
      rsid: 'rs234706',
      genotype: 'G/A (one copy)',
      plainMeaning:
        "Sarah's CBS enzyme may run faster than average, pushing more homocysteine down the sulfur route before it has a chance to be recycled.",
      technicalDetail:
        "CBS (cystathionine beta-synthase) catalyses the first committed step of transsulfuration, converting homocysteine to cystathionine. The C699T variant (rs234706, also noted as 699C>T) has been associated in some studies with increased CBS activity. An upregulated CBS drains homocysteine into the sulfur pathway faster than normal, reducing the pool available for methylation and SAMe production. Evidence is mixed and population-dependent; the effect is most pronounced when combined with high-dose sulfur-containing supplements.",
    },
    {
      gene: 'BHMT',
      variant: 'R239Q',
      rsid: 'rs3733890',
      genotype: 'G/A (one copy)',
      plainMeaning:
        "Sarah's backup recycling route — the shortcut that uses betaine to recover homocysteine — is less efficient, meaning the methylation cycle relies more heavily on the MTHFR route to keep up.",
      technicalDetail:
        "BHMT (betaine–homocysteine methyltransferase) provides an alternative methylation pathway that transfers a methyl group from betaine to homocysteine, regenerating methionine independently of folate and B12. The R239Q variant reduces BHMT enzyme efficiency. With both CBS upregulation and reduced BHMT backup, homocysteine is preferentially pushed into transsulfuration rather than recycled — amplifying sulfur pathway overload.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'Doing everything right, feeling worse',
      body: "Sarah is 41 and works in healthcare administration. She is not someone who ignores her health. After reading about methylation, she built a supplement stack that looked exactly right on paper: methylfolate, methylcobalamin, NAC, liposomal glutathione, magnesium. She gave it six months. By month three, she was more fatigued than when she started. By month five, a new anxiety had appeared — low-level, persistent, unexplained. Her jaw and shoulders held a tension she could not release. Her practitioner told her to give it more time.",
    },
    {
      type: 'narrative',
      heading: 'The clue her homocysteine hid',
      body: "Her homocysteine came back at 6.2 µmol/L — well inside the reference range, technically ideal. So nothing pointed to a methylation problem. But homocysteine being low does not mean the pathway is running well. It can also mean homocysteine is draining out the other end — being consumed faster than it is produced. When the sulfur pathway is overactive, homocysteine is pulled down so efficiently that the number looks fine even as the downstream byproducts accumulate.",
    },
    {
      type: 'narrative',
      heading: 'The sulfur trail',
      body: "Two details her standard workup missed. Her urinary sulfite was elevated — a sign that sulfite is being produced faster than her body can clear it. And she had already noticed, without connecting it to her supplements, that wine and dried fruit made her feel unwell. Sulphite sensitivity is not a random intolerance. It is a signal that the sulfur pathway is already under strain — and that adding high-dose sulfur-containing supplements to that system creates exactly the environment she had been living in for six months.",
    },
    {
      type: 'reveal',
      heading: 'The supplements were adding fuel',
      body: "NAC is a precursor to cysteine, which feeds directly into the sulfur pathway. Glutathione, when broken down in the gut, also releases cysteine. For most people these are helpful. For Sarah — with a CBS variant that already pushes homocysteine through the sulfur route faster, and a BHMT backup route that is less efficient — the extra cysteine load compounded an already overloaded pathway. Sulfite built up. Hydrogen sulfide accumulated. The fatigue, the anxiety, the tension: these are consistent with a nervous system under sulfur stress, not a system that needed more support.",
    },
    {
      type: 'reveal',
      heading: 'Why her homocysteine looked perfect',
      body: "The CBS variant creates a picture that looks reassuring on standard labs. Homocysteine is low-normal — the pathway appears to be working. But the methylation cycle upstream was being quietly depleted: less homocysteine available to recycle into methionine and SAMe. SAMe is the methyl donor for mood regulation, stress response, and dozens of other processes. A low-normal homocysteine with the wrong supplements can mean the system is draining, not thriving.",
    },
    {
      type: 'action',
      heading: 'What Sarah brings to her clinician',
      items: [
        "Pause NAC and liposomal glutathione — both add to the cysteine and sulfur load. Discuss with her clinician before restarting.",
        "Ask for urinary sulfite testing if not already done, to confirm sulfur pathway overload rather than assuming.",
        "Discuss molybdenum — a cofactor for sulfite oxidase, the enzyme that clears sulfite. Low molybdenum impairs clearance.",
        "Add taurine — it is already being produced in excess via the CBS route, but supplementing the active form supports GABA receptor activity and may address the anxiety and tension directly.",
        "Revisit methylfolate and methylcobalamin doses with her clinician — the goal is supporting the recycling route without overwhelming the sulfur route further.",
        "Track sulphite-sensitive food reactions (wine, dried fruit, processed meats) as a proxy for pathway load while adjusting the protocol.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Sarah's check-in: 14 days of tracking",
      goalLabel: 'Daily fatigue & anxiety check-in',
      caption:
        "Sarah logged fatigue and anxiety every morning. The first week looked the same as the previous six months. After pausing NAC and glutathione on day 8 and adding molybdenum, the pattern shifted.",
      days: [
        { day: 1,  scores: [{ label: 'Fatigue', value: 7 }, { label: 'Anxiety', value: 6 }], note: "Another morning waking up worse than when I went to bed. Shoulders already tight." },
        { day: 2,  scores: [{ label: 'Fatigue', value: 7 }, { label: 'Anxiety', value: 7 }], note: "Anxiety worse today. Can't figure out if it's stress or something else." },
        { day: 3,  scores: [{ label: 'Fatigue', value: 6 }, { label: 'Anxiety', value: 6 }], note: "Same. Took NAC at lunch and felt worse by evening — probably coincidence." },
        { day: 4,  scores: [{ label: 'Fatigue', value: 7 }, { label: 'Anxiety', value: 7 }], note: "Bad day. Jaw clenching at my desk without realising." },
        { day: 5,  scores: [{ label: 'Fatigue', value: 6 }, { label: 'Anxiety', value: 6 }], note: "A tiny bit less tense today. Skipped wine last night." },
        { day: 6,  scores: [{ label: 'Fatigue', value: 7 }, { label: 'Anxiety', value: 7 }], note: "Nope. Had a glass of wine — bad idea. Felt terrible by midnight." },
        { day: 7,  scores: [{ label: 'Fatigue', value: 6 }, { label: 'Anxiety', value: 6 }], note: "Practitioner appointment today. Showed her the urinary sulfite result." },
        { day: 8,  scores: [{ label: 'Fatigue', value: 6 }, { label: 'Anxiety', value: 5 }], note: "Paused NAC and glutathione this morning. Added molybdenum.", milestone: "Paused NAC + glutathione, started molybdenum" },
        { day: 9,  scores: [{ label: 'Fatigue', value: 6 }, { label: 'Anxiety', value: 5 }], note: "Maybe slightly less tense. Too early to call." },
        { day: 10, scores: [{ label: 'Fatigue', value: 5 }, { label: 'Anxiety', value: 4 }], note: "Shoulders noticeably looser today. First time in months I sat without tension." },
        { day: 11, scores: [{ label: 'Fatigue', value: 4 }, { label: 'Anxiety', value: 4 }], note: "Energy better. Not dramatically, but consistently better than before." },
        { day: 12, scores: [{ label: 'Fatigue', value: 4 }, { label: 'Anxiety', value: 3 }], note: "Anxiety quieter. The background hum is fading." },
        { day: 13, scores: [{ label: 'Fatigue', value: 3 }, { label: 'Anxiety', value: 3 }], note: "Slept without waking. Three nights in a row now." },
        { day: 14, scores: [{ label: 'Fatigue', value: 3 }, { label: 'Anxiety', value: 2 }], note: "Follow-up with practitioner next week. The data makes the conversation easier." },
      ],
    },
    {
      type: 'heatmap' as const,
      heading: 'Eight weeks of data — before and after',
      caption: "Each square is one day. Darker red means worse. Green means a better day. Gray means not logged. Week 5 is when Sarah paused NAC and glutathione.",
      weekLabels: [
        'Week 1', 'Week 2', 'Week 3', 'Week 4',
        'Week 5 — paused NAC + glutathione', 'Week 6', 'Week 7', 'Week 8',
      ],
      rows: [
        {
          symptom: 'Fatigue',
          //        W1                      W2                      W3                      W4                      W5                      W6                      W7                      W8
          days: [5,5,4,5,4,5,4,  5,4,5,5,4,5,4,  4,5,4,5,5,4,5,  5,4,5,4,5,5,4,  4,4,3,3,3,3,3,  3,2,2,3,2,3,2,  2,2,2,2,2,2,2,  2,1,1,2,1,1,1],
        },
        {
          symptom: 'Anxiety',
          days: [4,5,5,4,5,4,5,  5,4,5,5,4,5,4,  5,4,5,4,4,5,4,  4,5,4,5,4,4,5,  4,4,3,3,3,3,3,  3,2,3,2,2,2,2,  2,2,2,2,2,2,2,  1,1,1,1,1,2,1],
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'transsulfuration-sarah',
    label: 'Explore your CBS pathway in the Wellness Coach',
  },
}
